import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Zap, Building2, UserRound, ListOrdered } from "lucide-react";
import { http } from "@/lib/api";
import { useRealtimeTick } from "@/lib/realtime";
import { ShareChampionButton } from "@/components/ChampionShare";
import { useAuth } from "@/context/AuthContext";
import { useBadges, weekLabel } from "@/components/Badges";
import { UserAvatar } from "@/components/UserAvatar";
import { MONTH_NAMES, fmtNum } from "@/lib/helpers";

function LeaderTile({ leader, kind, title, period, sub, testId, delay }) {
  const Icon = kind === "month" ? Crown : Zap;
  const shareChamp = useMemo(() => (leader ? { ...leader, current: true } : null), [leader]);
  const tone = kind === "month" ? "text-amber-300 bg-amber-500/10 border-amber-500/25" : "text-cyan-300 bg-cyan-500/10 border-cyan-500/25";
  return (
    <div className={`rounded-2xl border border-slate-800 bg-slate-900/60 p-6 flex flex-col gap-5 animate-fade-up ${delay}`} data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500">{period}</div>
          <div className="font-display font-semibold text-slate-100 mt-0.5">{title}</div>
        </div>
        <div className="flex items-center gap-2">
          {shareChamp && <ShareChampionButton champ={shareChamp} label="Paylaş" className="px-3 py-1 text-xs" testId={`${testId}-share`} />}
          <span className={`h-9 w-9 rounded-xl border flex items-center justify-center ${tone}`}><Icon size={16} /></span>
        </div>
      </div>
      {leader ? (
        <Link to={`/profile/${leader.user_id}`} className="flex items-center gap-4 group" data-testid={`${testId}-link`}>
          <UserAvatar path={leader.photo_path} name={leader.name} className="h-16 w-16 shrink-0" rounded="rounded-2xl" />
          <div className="min-w-0 flex-1">
            <div className="font-display text-lg font-bold leading-tight truncate group-hover:text-blue-300 transition-colors" data-testid={`${testId}-name`}>{leader.name}</div>
            <div className="text-xs text-slate-400 truncate">{leader.branch_name || "-"}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-display text-3xl font-bold tabular-nums leading-none" data-testid={`${testId}-qty`}>{fmtNum(leader.qty)}</div>
            <div className="text-[11px] text-slate-500 mt-1">adet{sub ? ` · ${sub}` : ""}</div>
          </div>
        </Link>
      ) : (
        <div className="h-16 flex items-center text-sm text-slate-500" data-testid={`${testId}-empty`}>Henüz satış yok</div>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-slate-400"><Icon size={14} /> {title}</div>
      <div className="grid md:grid-cols-2 gap-4">{children}</div>
    </section>
  );
}

function RankList({ title, icon: Icon, rows, testId, render }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-200 mb-3"><Icon size={15} className="text-slate-400" /> {title}</div>
      {rows.length ? (
        <ol className="divide-y divide-slate-800/80">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center gap-3 py-2.5" data-testid={`${testId}-row-${i + 1}`}>
              <span className={`w-6 text-center font-mono text-sm ${i === 0 ? "text-amber-300 font-bold" : "text-slate-500"}`}>{i + 1}</span>
              {render(r)}
              <span className="ml-auto font-display font-bold tabular-nums">{fmtNum(r.qty)}<span className="text-[11px] font-normal text-slate-500 ml-1">adet</span></span>
            </li>
          ))}
        </ol>
      ) : <div className="py-6 text-sm text-slate-500" data-testid={`${testId}-empty`}>Henüz satış yok</div>}
    </div>
  );
}

function Rankings() {
  const [period, setPeriod] = useState("month");
  const [d, setD] = useState({ staff: [], branches: [] });
  const tick = useRealtimeTick((m) => m.path.startsWith("/api/sales"));
  useEffect(() => { http.get("/rankings", { params: { period } }).then((r) => setD(r.data)).catch(() => {}); }, [period, tick]);
  return (
    <section className="space-y-3 animate-fade-up delay-300" data-testid="home-rankings">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-slate-400"><ListOrdered size={14} /> İlk 5 Sıralama</div>
        <div className="flex rounded-full border border-slate-800 bg-slate-900/60 p-0.5 text-xs" data-testid="rankings-period">
          {[["month", "Bu Ay"], ["week", "Bu Hafta"]].map(([k, l]) => (
            <button key={k} onClick={() => setPeriod(k)} data-testid={`rankings-period-${k}`}
                    className={`px-3 py-1 rounded-full transition-colors ${period === k ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <RankList title="Personel" icon={UserRound} rows={d.staff} testId="rank-staff" render={(r) => (
          <Link to={`/profile/${r.user_id}`} className="flex items-center gap-3 min-w-0 hover:text-blue-300 transition-colors">
            <UserAvatar path={r.photo_path} name={r.name} className="h-8 w-8 shrink-0" rounded="rounded-lg" />
            <span className="min-w-0"><span className="block text-sm font-medium truncate">{r.name}</span><span className="block text-[11px] text-slate-500 truncate">{r.branch_name || "-"}</span></span>
          </Link>
        )} />
        <RankList title="Şubeler" icon={Building2} rows={d.branches} testId="rank-branches" render={(r) => (
          <span className="min-w-0"><span className="block text-sm font-medium truncate">{r.name}</span><span className="block text-[11px] text-slate-500 truncate">{r.manager || "Müdür atanmamış"}</span></span>
        )} />
      </div>
    </section>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const b = useBadges();
  const now = new Date();
  const month = `${MONTH_NAMES[now.getMonth()]} ${now.getFullYear()}`;
  const week = b.week_current ? weekLabel(b.week_current) : b.mgr_week_current ? weekLabel(b.mgr_week_current) : "Bu hafta";
  return (
    <div className="max-w-5xl space-y-10" data-testid="home-screen">
      <header className="animate-fade-up">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">{month}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Liderler</h1>
        <p className="text-slate-400 text-sm mt-2">Hoş geldiniz, {user?.full_name}. Şirket genelinde ayın ve haftanın öne çıkanları.</p>
      </header>
      <Section icon={UserRound} title="Personel">
        <LeaderTile leader={b.current} kind="month" title="Ayın Lideri" period={month} testId="home-month-leader" delay="delay-100" />
        <LeaderTile leader={b.week_current} kind="week" title="Haftanın Lideri" period={week} testId="home-week-leader" delay="delay-100" />
      </Section>
      <Section icon={Building2} title="Şube Müdürleri">
        <LeaderTile leader={b.mgr_current} kind="month" title="Ayın Müdürü" period={month} sub="şube toplamı" testId="home-mgr-month-leader" delay="delay-200" />
        <LeaderTile leader={b.mgr_week_current} kind="week" title="Haftanın Müdürü" period={week} sub="şube toplamı" testId="home-mgr-week-leader" delay="delay-200" />
      </Section>
      <Rankings />
    </div>
  );
}
